import { FC, useEffect, useRef, RefObject, memo } from 'react';
import classNames from 'classnames';

import { DropdownItemDataType, DropdownPlacement } from '@zvuk-b2b/react-uikit/ui/Dropdown';

import CompanyTagsList, { CompanyTagsListProps } from './CompanyTagsList';
import useTagFiltersPresenter from './useTagFiltersPresenter';
import useTagFiltersController from './useTagFiltersController';
import TagsDropdown from './TagsDropdown';

import styles from './index.module.scss';

export interface ITagFiltersState extends Partial<CompanyTagsListProps> {
  allSelectedTags?: DropdownItemDataType[];
}

export type TagFiltersProps = {
  className?: string;
  buttonClassName?: string;
  onChange?: (tags: DropdownItemDataType[]) => void;
  tagIds?: string[];
  pageType: 'point' | 'ad_campaign' | 'video_streams';
  onlyIcon?: boolean;
  placement?: DropdownPlacement;
  onSetCompanyListTagsProps?: (companyListProps: ITagFiltersState) => void;
  withList?: boolean;
  listRef?: RefObject<HTMLInputElement | null> | null;
};

const TagFilters: FC<TagFiltersProps> = ({
  className,
  buttonClassName,
  onChange,
  tagIds,
  pageType,
  onlyIcon = false,
  placement,
  onSetCompanyListTagsProps,
  withList = true,
  listRef,
}) => {
  const rootRef = useRef<HTMLInputElement | null>(null);

  const {
    companyId,
    companyTags,
    paginationInfo,
    companyTagsLoading,
    requestCompanyTags,
    loadMoreCompanyTags,
    onFilterExecute,
  } = useTagFiltersPresenter();

  const {
    isDesktop,
    visibleTagTitleLength,
    setIsExpanded,
    isExpanded,
    filterOptions,
    onDropdownClose,
    onSearchChange,
    onSelect,
    maxVisibleOptions,
    menuMargin,
    showAllTags,
    onDisableTag,
    expandCallback,
    clearAll,
    moreTagsCount,
    shownTags,
    allSelectedTags,
  } = useTagFiltersController({
    companyId,
    companyTags,
    paginationInfo,
    requestCompanyTags,
    loadMoreCompanyTags,
    onFilterExecute,
    onChange,
    tagIds,
    pageType,
    rootRef: listRef || rootRef,
    dropdownInTagsRow: !onlyIcon,
  });

  useEffect(() => {
    onSetCompanyListTagsProps?.({
      onDisableTag,
      visibleTitleLength: visibleTagTitleLength,
      selectedTags: shownTags,
      moreTagsCount,
      showAllTags,
      expandCallback,
      clearAll,
      allSelectedTags,
    });
  }, [
    onDisableTag,
    visibleTagTitleLength,
    moreTagsCount,
    expandCallback,
    clearAll,
    shownTags.length,
    showAllTags,
    allSelectedTags,
  ]);

  return (
    <div
      className={classNames(styles.FiltersTags, className, showAllTags && styles.FiltersTagsExpanded)}
      data-is-desktop={isDesktop}
      ref={rootRef}>
      <TagsDropdown
        buttonClassName={buttonClassName}
        isExpanded={isExpanded}
        filterOptions={filterOptions}
        maxVisibleOptions={maxVisibleOptions}
        menuMargin={menuMargin}
        isLoading={companyTagsLoading}
        shownTags={shownTags}
        onlyIcon={onlyIcon || !isDesktop}
        placement={placement}
        setIsExpanded={setIsExpanded}
        onDropdownClose={onDropdownClose}
        onSearchChange={onSearchChange}
        onSelect={onSelect}
      />

      {withList && (
        <CompanyTagsList
          onDisableTag={onDisableTag}
          visibleTitleLength={visibleTagTitleLength}
          selectedTags={shownTags}
          moreTagsCount={moreTagsCount}
          showAllTags={showAllTags}
          expandCallback={expandCallback}
          clearAll={clearAll}
        />
      )}
    </div>
  );
};

export default memo(TagFilters);
